import React from "react";
import { useTranslation } from "react-i18next";
import { Languages, ArrowRight } from "lucide-react";

/**
 * FallbackBanner — shown on top of an article when the current locale has
 * no translated content and we're serving the Dutch fallback. Reduces SEO
 * soft-404 risk and is honest to the reader.
 *
 * Props:
 *  - slug: article slug (used for the NL link target).
 *  - basePath: route prefix (e.g. "/blog" or "/kennisbank").
 */
export const FallbackBanner = ({ slug, basePath = "/blog" }) => {
  const { t } = useTranslation();
  // Always link to the NL (root) version — never to the current locale.
  const nlHref = `https://equisight.nl${basePath}/${slug}`;
  return (
    <aside
      data-testid="fallback-banner"
      className="mx-auto mt-8 max-w-3xl px-6 lg:px-10"
    >
      <div className="flex flex-col gap-3 rounded-2xl border border-[hsl(var(--sand))]/40 bg-[hsl(var(--sand))]/10 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Languages
            className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--sand-dark))]"
            strokeWidth={1.7}
          />
          <div>
            <p className="font-display text-base text-[hsl(var(--ink))]">
              {t("blog.fallbackBanner.title")}
            </p>
            <p className="mt-1 text-sm text-[hsl(var(--ink-soft))]">
              {t("blog.fallbackBanner.body")}
            </p>
          </div>
        </div>
        <a
          data-testid="fallback-banner-cta"
          href={nlHref}
          className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full bg-[hsl(var(--forest))] px-4 py-2 text-sm font-medium text-[hsl(var(--background))] transition-all hover:-translate-y-0.5 hover:bg-[hsl(var(--forest-dark))] sm:self-center"
        >
          {t("blog.fallbackBanner.cta")} <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </aside>
  );
};

export default FallbackBanner;
